import { Box, Button, CircularProgress, Grid2, Modal, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { viewPDF } from "../../../utils/viewPDF";
import SuccessModal from "../../../utils/SuccessModal";
import ErrorModal from "../../../utils/ErrorModal";
import { handleExportPDF } from "../../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const EditPreviewTender = ({
  draft,
  workItems,
  setStep,
  user,
  approvers,
  openers,
  awardees,
  noOfOpeners,
  filename,
  fileNames,
}) => {
  const pdfRef = useRef();
  const [approversData, setApproversData] = useState({
    purchaseHead: "",
    technicalHead: "",
    financeHead: "",
  });
  const [awardeesData, setAwardeesData] = useState({
    awardee1: "",
    awardee2: "",
  });
  const [openersData, setOpenersData] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState();
  const handleClose = () => setOpen(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchApprovers = async () => {
      try {
        const [res1, res2, res3] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.purchaseHead}`,
            {
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.technicalHead}`,
            {
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.financeHead}`,
            {
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }
          ),
        ]);

        setApproversData({
          purchaseHead: res1.data.name,
          technicalHead: res2.data.name,
          financeHead: res3.data.name,
        });
      } catch (error) {
        console.error(error);
      }
    };

    fetchApprovers();
  }, [draft]);

  useEffect(() => {
    const fetchAwardees = async () => {
      try {
        let awardee1Name = "N/A";
        if (awardees.awardee1) {
          const res1 = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${awardees.awardee1}`,
            {
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }
          );
          awardee1Name = res1.data.name || "N/A";
        }

        const res2 = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${awardees.awardee2}`,
          {
            headers: { Authorization: `Bearer ${user.accessToken}` },
          }
        );

        setAwardeesData({
          ...awardees,
          awardee1: awardee1Name,
          awardee2: res2.data.name,
        });
      } catch (error) {
        console.error(error);
      }
    };

    if (awardees.awardee1 || awardees.awardee2) {
      fetchAwardees();
    }
  }, [draft]);

  useEffect(() => {
    const fetchOpeners = async () => {
      try {
        const responses = await Promise.all(
          openers.map((opener) =>
            axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${opener}`,
              { headers: { Authorization: `Bearer ${user.accessToken}` } }
            )
          )
        );
        const openerDetails = responses.map((res) => res.data);
        setOpenersData(openerDetails);
      } catch (error) {
        console.log(error);
      }
    };

    if (openers?.length > 0) {
      fetchOpeners();
    }
  }, [draft]);

  const groupedWorkItems = workItems?.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const handleNext = async () => {
    setBtnLoading(true);
    try {
      const indexedData = workItems.map((item, index) => ({
        ...item,
        description: item?.description.slice(0, 2000),
        itemOrder: index,
      }));
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/tender/update?tenderId=${draft.tenderId}`,
        {
          title: draft.title,
          username: user.username,
          shortDesc: draft.shortDesc,
          longDesc: draft.longDesc,
          tenderPurchaseDate: draft.tenderPurchaseDate,
          tenderPublishDate: draft.tenderPublishDate,
          bidSubmissionStartDate: draft.bidSubmissionStartDate,
          bidSubmissionEndDate: draft.bidSubmissionEndDate,
          bidOpeningDate: draft.bidOpeningDate,
          bidOfferValidity: draft.bidOfferValidity,
          deptName: draft.deptName,
          contactEmail: draft.contactEmail,
          itemCategory: draft.itemCategory,
          contractPeriod: draft.contractPeriod,
          sellerDoc: draft.sellerDoc,
          type: draft.type,
          technicalExpiry: draft.technicalExpiry,
          bidValue: draft.bidValue,
          evalMethod: draft.evalMethod,
          arbitrationClause: draft.arbitrationClause,
          mediationClause: draft.mediationClause,
          emdBank: draft.emdBank,
          emdAmount: draft.emdAmount,
          accountNumber: draft.accountNumber,
          ifsc: draft.ifsc,
          accountName: draft.accountName,
          accountType: draft.accountType,
          pbgBank: draft.pbgBank,
          pbgPercentage: draft.pbgPercentage,
          pbgDuration: draft.pbgDuration,
          annualTurnover: draft.annualTurnover,
          expYears: draft.expYears,
          expSimilarWork: draft.expSimilarWork,
          totalEmp: draft.totalEmp,
          ts1Title: draft.ts1Title,
          ts1Filename: filename.ts1,
          ts1: draft.ts1,
          ts2Title: draft.ts2Title,
          ts2Filename: filename.ts2,
          ts2: draft.ts2,
          ts3Title: draft.ts3Title,
          ts3Filename: filename.ts3,
          ts3: draft.ts3,
          ts4Title: draft.ts4Title,
          ts4Filename: filename.ts4,
          ts4: draft.ts4,
          ts5Title: draft.ts5Title,
          ts5Filename: filename.ts5,
          ts5: draft.ts5,
          ts6Title: draft.ts6Title,
          ts6Filename: filename.ts6,
          ts6: draft.ts6,
          ts7Title: draft.ts7Title,
          ts7Filename: filename.ts7,
          ts7: draft.ts7,
          ts8Title: draft.ts8Title,
          ts8Filename: filename.ts8,
          ts8: draft.ts8,
          buyerTNC: draft.buyerTNC,
          tnc: draft.tnc,
          tncFilename: fileNames.tnc,
          serviceAgreement: draft.serviceAgreement,
          serviceAgreementFilename: fileNames.serviceAgreement,
          protection: draft.protection,
          protectionFilename: fileNames.protection,
          approvers: [
            approvers.purchaseHead,
            approvers.technicalHead,
            approvers.financeHead,
          ],
          noOfOpeners: noOfOpeners,
          openers: openers,
          awardee1: awardees.awardee1,
          awardee2: awardees.awardee2,
          workItemDetails: indexedData,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setOpenConfirm(false);
      setOpen(true);
      setMsg("Tender Updated and Sent to Purchase Head for Approval");
    } catch (error) {
      setOpenConfirm(false);
      setErrorModal(true);
      setErrorMsg(
        error.response?.data?.Exception || "Failed to create tender."
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year} ${time}`;
  };

  const grandTotal =
    workItems &&
    workItems
      .reduce((sum, row) => sum + parseFloat((row.quantity * row.rate) || 0), 0)
      .toFixed(2);

  return (
    <div style={{ position: "relative" }}>
      {exporting && <div className="uploading-div">Exporting PDF, this may take a while. <CircularProgress color="#fff" /></div>}
      {btnLoading && <div className="loading-div uploading-div">Submitting Tender... <CircularProgress color="#fff" /></div>}
      <h2>Preview Tender</h2>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true)
              await handleExportPDF(pdfRef, "Tender_Details")
            } catch (error) {
              console.log(error)
            } finally {
              setExporting(false)
            }
          }}
        >
          Export PDF
        </Button>
      </div>
      <div ref={pdfRef}>
        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">1. Tender Details</Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Title
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.title}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Short Description
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.shortDesc}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Long Description
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.longDesc}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Tender Purchase Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDate(draft.tenderPurchaseDate)}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Tender Publish Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDate(draft.tenderPublishDate)}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Bid Submission Start Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDateTime(draft.bidSubmissionStartDate)}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Bid Submission End Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDateTime(draft.bidSubmissionEndDate)}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Bid Opening Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDateTime(draft.bidOpeningDate)}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Bid Offer Validity (From End Date) (In Days)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.bidOfferValidity}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Department Name
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.deptName}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Contact Person Email
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.contactEmail}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Item Category
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.itemCategory}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Contract Period (In Months)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.contractPeriod}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Documents required from seller
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.sellerDoc}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Type of Tender
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.type}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Time allowed for Technical Clarifications (in days)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.technicalExpiry}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Estimated Bid Value (in &#8377;)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.bidValue !== undefined
                ? new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(draft.bidValue)
                : "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Evaluation Method
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.evalMethod}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Arbitration Clause
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.arbitrationClause ? "Yes" : "No"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Mediation Clause
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.mediationClause ? "Yes" : "No"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Will the tender cost be borne by you?
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.isBorne ? "Yes" : "No"}
            </Grid2>

            {!draft?.isBorne && (
              <>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Payment of Suppliers will be received in?
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.bank === "ARG"
                    ? "ARG Account"
                    : "Own Account"}
                </Grid2>
              </>
            )}
            {draft?.bank === "ARG" && (
              <>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Tender Purchase Amount
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseAmount}
                </Grid2>
              </>
            )}
            {draft?.bank === "OWN" && (
              <>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Bank Name
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseBank}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Bank Account Number
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseAccountNumber}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  IFSC
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseIfsc}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Beneficiary Name
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseAccountName}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  Account Type
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {draft && draft.purchaseAccountType}
                </Grid2>
              </>
            )}
          </Grid2>

          <Typography className="form-title">2. EMD Details</Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Advisory Bank
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.emdBank}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              EMD Amount (in &#8377;)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.emdAmount !== undefined
                ? new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(draft.emdAmount)
                : "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Bank Account Number
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.accountNumber}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              IFSC
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ifsc}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Beneficiary Name
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.accountName}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Account Type
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.accountType}
            </Grid2>
          </Grid2>

          <Typography className="form-title">3. ePBG Details</Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Advisory Bank
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.pbgBank}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              ePBG Percentage
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.pbgPercentage && `${draft.pbgPercentage}%`}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Duration of ePBG required (Months)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.pbgDuration}
            </Grid2>
          </Grid2>

          <Typography className="form-title">
            4. Additional Qualification/Data Required
          </Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Annual Turnover (in &#8377;)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.annualTurnover !== undefined
                ? new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(draft.annualTurnover)
                : "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Total Number of years of experience
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.expYears}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Experience of similar work
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.expSimilarWork}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Number of Employees
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.totalEmp}
            </Grid2>
          </Grid2>

          <Typography className="form-title">
            5. Technical Specifications
          </Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              TS-1 {draft && draft.ts1Title && <span>({draft.ts1Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.ts1 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts1, user)}
                >
                  {draft.ts1Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-2 {draft && draft.ts2Title && <span>({draft.ts2Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts2 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts2, user)}
                >
                  {draft.ts2Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-3 {draft && draft.ts3Title && <span>({draft.ts3Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts3 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts3, user)}
                >
                  {draft.ts3Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-4 {draft && draft.ts4Title && <span>({draft.ts4Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts4 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts4, user)}
                >
                  {draft.ts4Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-5 {draft && draft.ts5Title && <span>({draft.ts5Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts5 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts5, user)}
                >
                  {draft.ts5Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-6 {draft && draft.ts6Title && <span>({draft.ts6Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts6 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts6, user)}
                >
                  {draft.ts6Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-7 {draft && draft.ts7Title && <span>({draft.ts7Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts7 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts7, user)}
                >
                  {draft.ts7Filename}
                </Button>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              TS-8 {draft && draft.ts8Title && <span>({draft.ts8Title})</span>}
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.ts8 && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.ts8, user)}
                >
                  {draft.ts8Filename}
                </Button>
              )}
            </Grid2>
          </Grid2>

          <Typography className="form-title">
            6. Buyer Added Bid Specific Terms and Conditions
          </Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Buyer Added text based clauses
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.buyerTNC}
            </Grid2>
          </Grid2>

          <Typography className="form-title">
            7. General Terms and Conditions
          </Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Terms and conditions
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.tnc && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.tnc, user)}
                >
                  {draft.tncFilename}
                </Button>
              )}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Service Level Agreement
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.serviceAgreement && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.serviceAgreement, user)}
                >
                  {draft.serviceAgreementFilename}
                </Button>
              )}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Safety and Environment Protection
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.protection && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.protection, user)}
                >
                  {draft.protectionFilename}
                </Button>
              )}
            </Grid2>
          </Grid2>
        </Box>

        <h2>Work Items Details</h2>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Item Name</th>
                <th>Description</th>
                <th>Section</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Rate (in &#8377;)</th>
                <th>Total (in &#8377;)</th>
              </tr>
            </thead>
            <tbody>
              {groupedWorkItems &&
                Object.entries(groupedWorkItems).map(
                  ([section, items], sectionIndex) => {
                    const sectionTotal = items.reduce(
                      (sum, item) => sum + item.rate * item.quantity,
                      0
                    );
                    return (
                      <React.Fragment key={sectionIndex}>
                        <tr
                          style={{
                            backgroundColor: "#d8e9f0",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            colSpan={6}
                            style={{ textAlign: "left", padding: "10px" }}
                          >
                            {section}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {sectionTotal.toFixed(2)}
                          </td>
                        </tr>
                        {items
                          .sort((a, b) => a.itemOrder - b.itemOrder)
                          .map((item, index) => (
                            <tr key={index} style={{ textAlign: "center" }}>
                              <td style={{ padding: "20px" }}>
                                {item.itemName}
                              </td>
                              <td>{item.description}</td>
                              <td>{item.section}</td>
                              <td>{item.unit}</td>
                              <td>{item.quantity}</td>
                              <td>{item.rate}</td>
                              <td>{(item.rate * item.quantity).toFixed(2)}</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  }
                )}
              <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                <td
                  colSpan={6}
                  style={{ textAlign: "right", paddingRight: "10px" }}
                >
                  Grand Total (in &#8377;):
                </td>
                <td
                  style={{
                    width: "150px",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {grandTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Approvers</Typography>
          <Grid2 container>
            {/* <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{borderTop: '1px solid #000'}}>
                      BUYER
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid" sx={{borderTop: '1px solid #000', borderRight: '1px solid #000'}}>
                      {tender.orgUser.name}
                    </Grid2> */}
            {approversData && (
              <React.Fragment>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderTop: "1px solid #000" }}
                >
                  PURCHASE HEAD
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{
                    borderTop: "1px solid #000",
                    borderRight: "1px solid #000",
                  }}
                >
                  {approversData.purchaseHead || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  TECHNICAL HEAD
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {approversData.technicalHead || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINANCE HEAD
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {approversData.financeHead || "Loading..."}
                </Grid2>
              </React.Fragment>
            )}
            {awardeesData && (
              <React.Fragment>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 1
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {awardeesData.awardee1 || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 2
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {awardeesData.awardee2 || "Loading..."}
                </Grid2>
              </React.Fragment>
            )}
          </Grid2>
        </Box>
        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Bid Openers</Typography>

          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 12 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              Total Bid Openers: {noOfOpeners}
            </Grid2>
            {openersData
              ? openersData.map((opener, i) => (
                  <React.Fragment key={i}>
                    <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                      Bid Openers
                    </Grid2>
                    <Grid2
                      size={{ xs: 12, sm: 6 }}
                      className="preview-grid"
                      sx={{ borderRight: "1px solid #000" }}
                    >
                      {opener.name || "Loading..."}
                    </Grid2>
                  </React.Fragment>
                ))
              : null}
          </Grid2>
        </Box>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={() => setOpenConfirm(true)}
          disabled={btnLoading}
        >
          Submit
        </Button>
      </Box>

      <Modal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to submit these details?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              variant="outlined"
              onClick={() => setOpenConfirm(false)}
            >
              No
            </Button>
            <Button
              className="btn-contained"
              onClick={handleNext}
              disabled={btnLoading}
            >
              {btnLoading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <SuccessModal
        open={open}
        handleClose={handleClose}
        msg={msg}
        nav={"/dashboard"}
      />

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default EditPreviewTender;
